import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';

import { JwtHelperService } from '@auth0/angular-jwt';
import { MessageService } from '@services/http/message.service';
import { NotificationService } from '@services/http/notification.service';
import { StateService } from '@services/Logic/state.service';
import { StorageService } from '../Logic/storage.service';
import { SocketService } from './socket.service';
import { UserSettingsService } from '@services/http/userSettings.service';

import { environment } from '../../../environments/environment';
import { tap } from 'rxjs/operators';
import { UserSettingsModel } from '@models/user/userSettings.model';

@Injectable()
export class AuthService implements OnDestroy {
  decodedToken: any;

  public authStatusSource = new BehaviorSubject<boolean>(
    this.isAuthenticated(),
  );
  public authStatus$ = this.authStatusSource.asObservable();
  private destroy$ = new Subject<void>();
  constructor(
    private http: HttpClient,
    private storage: StorageService,
    private socketService: SocketService,
    private messageService: MessageService,
    private notificationService: NotificationService,
    private userSettingsService: UserSettingsService,
    private stateService: StateService,
  ) {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  login(data: { email: string; password: string }): Observable<any> {
    const url = `${environment.apiUrl}/users/login`;

    return this.http.post<any>(url, data).pipe(
      tap(() => {
        this.authStatusSource.next(this.isAuthenticated());
      }),
    );
  }

  authenticateIfNecessary(token: string) {
    if (!this.storage.authToken) {
      this.storage.authToken = token;
    }
    this.authStatusSource.next(this.isAuthenticated());
  }

  getToken(): string {
    return this.storage.authToken;
  }

  getDecodedToken(): any {
    if (!this.decodedToken) {
      const token = this.getToken();
      if (!token) {
        return null;
      }
      try {
        const helper = new JwtHelperService();
        this.decodedToken = helper.decodeToken(token);
      } catch (err) {
        return this.logout();
      }
    }
    return this.decodedToken;
  }

  getUserIdFromAuthToken(): number {
    const decodedToken = this.getDecodedToken();
    if (!decodedToken) {
      return null;
    }
    return decodedToken.userId;
  }

  logout(): void {
    if (this.isAuthenticated()) {
      this.socketService.disconnectSocket();
      this.decodedToken = null;
      // Keep lang in storage after you logout
      const { lang } = this.storage;
      this.storage.clear();
      this.storage.lang = lang;
      window.location.reload();
    }
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    try {
      const helper = new JwtHelperService();
      const isExpired = helper.isTokenExpired(token);
      return !isExpired;
    } catch (err) {
      this.logout();
      return false;
    }
  }

  getUserSettingsdata() {
    // connect socket
    this.socketService.connectSocket();

    // get number of unread messages
    this.messageService
      .getNewMessagesCount()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.stateService.showNewMessagesHeader(res.numMessages);
      });

    // get new notifications
    this.notificationService
      .getNewNotificationCount()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.stateService.initNewNotificationsHeader(res);
      });

    // get user language
    this.userSettingsService
      .getPreferredLanguage()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        // Prioritize browser Choosen language before auth
        if (this.storage?.lang?.value !== res.lang) {
          this.stateService.initLanguage(this.storage.lang.value);
        } else {
          this.stateService.initLanguage(res);
        }
      });

    this.userSettingsService
      .getSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: UserSettingsModel) => {
        // Save user settings to localstorage
        if (this.storage && res) {
          this.storage.userSettings = res;
        }
      });
  }
}
